import { useTheme } from '../hooks/useTheme';
import { COLORS, LAYOUT } from '../styles/tokens';

interface MenuButtonProps {
  open: boolean;
  onClick: () => void;
  color: string;
}

/** Hamburger / close control used by the mobile header and the catalog top nav. */
export function MenuButton({ open, onClick, color }: MenuButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      className="vx-menu-btn"
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      onClick={onClick}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        border: 'none',
        background: 'transparent',
        color,
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        padding: 0,
      }}
    >
      {open ? <CloseGlyph /> : <MenuGlyph />}
    </button>
  );
}

interface MobileHeaderProps {
  menuOpen: boolean;
  onToggle: () => void;
}

/** Compact top bar shown on phones when the catalog TopNav is not mounted. */
export function MobileHeader({ menuOpen, onToggle }: MobileHeaderProps): React.JSX.Element {
  const { isDark, toggle } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  return (
    <header
      className="vx-mobile-header"
      style={{
        height: `${LAYOUT.mobileHeaderHeight}px`,
        backgroundColor: colors.card,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: '8px',
        flexShrink: 0,
        zIndex: 45,
      }}
    >
      <MenuButton open={menuOpen} onClick={onToggle} color={colors.text} />
      <img
        src={isDark ? '/brand/vertex-stacked-on-dark.svg' : '/brand/vertex-stacked-on-light.svg'}
        alt="Vertex"
        style={{ width: '36px', height: '36px', objectFit: 'contain' }}
      />
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {isDark ? <SunGlyph /> : <MoonGlyph />}
      </button>
    </header>
  );
}

/** Dimmed overlay that closes the drawer when tapped. */
export function NavBackdrop({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      className="vx-nav-backdrop"
      aria-label="Close menu"
      onClick={onClick}
    />
  );
}

function MenuGlyph(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function CloseGlyph(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function SunGlyph(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F4C43F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonGlyph(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B9BD5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
